import React, {useState} from 'react';
import {useLocation, useNavigate} from 'react-router-dom';
import {Dropdown} from 'react-bootstrap';
import {SvgIcon} from '../icons/svgIcon';
import {Icons} from '../icons/icons';
import {HibuUiConfig} from '../config/config-types';
import {useTranslation} from 'react-i18next';
import {useLocalizationUtil} from '../localizationUtil';
import {ConditionalRender} from '../common/conditional-render/ConditionalRender';
import {Language} from '../i18nTypes';
import {useAuthContext} from '../auth/AuthContextProvider';
import updateLanguageActionOnLocalStorage from '../i18n-service';
import {HibuTooltip} from '../common/hibuTooltip/hibuTooltip';
import {UserManual} from './userManual';
import {HibuButton} from '../common/hibuButton/hibuButton';

type HeaderMenuProps = {
    config: HibuUiConfig;
    isLandingPage: boolean;
};

export const UserMenu = React.memo(({config, isLandingPage}: HeaderMenuProps) => {
    const location = useLocation();
    const navigate = useNavigate();
    const {t} = useTranslation();
    const [selectedLanguage, setSelectedLanguage] = useState<Language>(useLocalizationUtil().selectedLanguage);
    const [showUserManual, setShowUserManual] = useState<boolean>(false);
    const {firstName, lastName, authService} = useAuthContext();

    const navigateToSettingsPage = () => {
        navigate('/settings', {state: {previousPath: location.pathname + location.search + location.hash}});
    };

    const doLogout = () => {
        void authService?.logout()?.catch((error) => console.error('Logout failed', error));
    };

    const languageOptionsMap = {
        [Language.GERMAN]: {value: Language.GERMAN, label: 'De'},
        [Language.ENGLISH]: {value: Language.ENGLISH, label: 'En'},
    };

    const languageOptions = Object.values(languageOptionsMap);

    const initials = () => {
        return firstName.substring(0, 1) + lastName.substring(0, 1);
    };

    const fullName = () => {
        return firstName + ' ' + lastName;
    };

    const changeShowUserManual = () => {
        setShowUserManual(!showUserManual);
    };

    const closeUserManual = () => {
        setShowUserManual(false);
    };

    const changeLanguage = (selectedOption: string | null) => {
        setSelectedLanguage(selectedOption as Language);
        updateLanguageActionOnLocalStorage(selectedOption as Language);
    };

    const shouldRenderSettings = config.settings.enabled;
    const shouldRenderLanguageSwitcher = config.languageSwitcher?.enabled;
    const shouldRenderLogout = authService ? authService.isEnabled() : false;

    if (!shouldRenderSettings && !shouldRenderLanguageSwitcher && !shouldRenderLogout) {
        return (
            <>
                <HibuButton visible={true} icon={Icons.QUESTION} onClick={changeShowUserManual}
                            className={'documentation-button'}/>
                <UserManual showUserManual={showUserManual} onClose={closeUserManual}></UserManual>
            </>
        );
    }

    return (
        <Dropdown id="user-dropdown" title={''} className="navigation-dropdown">
            <Dropdown.Toggle
                className={isLandingPage ? 'navigation-toggle landing-page' : 'navigation-toggle search-page'}>
                <HibuTooltip tooltipKey={fullName()} placement={'left'} key={fullName()}>
                    <p className="initials">{initials()}</p>
                </HibuTooltip>
            </Dropdown.Toggle>
            <Dropdown.Menu align="end" className="navigation-menu">
                <ConditionalRender shouldRender={true}>
                    <>
                        <Dropdown.Item onClick={changeShowUserManual}>
                            {t('navigation.documentation')}
                        </Dropdown.Item>
                        <UserManual showUserManual={showUserManual} onClose={closeUserManual}></UserManual>
                    </>
                </ConditionalRender>
                <ConditionalRender shouldRender={shouldRenderSettings}>
                    <Dropdown.Item eventKey="1" onClick={navigateToSettingsPage}>
                        {t('navigation.settings')}
                    </Dropdown.Item>
                </ConditionalRender>
                <ConditionalRender shouldRender={shouldRenderLanguageSwitcher}>
                    <Dropdown id="language-dropdown" onSelect={(eventKey) => changeLanguage(eventKey)} title={''}
                              className="navigation-dropdown">
                        <>
                            <Dropdown.Toggle className={'language-toggle'}>
                                <span>{t('navigation.language')}</span>
                                <SvgIcon icon={Icons.CHEVRON_DOWN} id="icon-chevron"/>
                            </Dropdown.Toggle>
                            <Dropdown.Menu align="end" className="navigation-menu">
                                {languageOptions.map(item => {
                                    return (
                                        <Dropdown.Item key={item.value} eventKey={item.value}
                                                       active={selectedLanguage === item.value}>
                                            {t('navigation.' + item.value)}
                                        </Dropdown.Item>
                                    );
                                })}
                            </Dropdown.Menu>
                        </>
                    </Dropdown>
                </ConditionalRender>
                <ConditionalRender shouldRender={shouldRenderLogout}>
                    <>
                        <Dropdown.Divider/>
                        <Dropdown.Item eventKey="3" onClick={doLogout}>
                            {t('navigation.logout')}
                        </Dropdown.Item>
                    </>
                </ConditionalRender>
            </Dropdown.Menu>
        </Dropdown>
    );
});
